import classNames from 'classnames'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { usePopper } from 'react-popper'

type TooltipProps = {
  children: React.ReactElement<any>
  content: React.ReactNode
  disabled?: boolean
  trigger?: 'click' | 'hover'
  placement?: 'top' | 'bottom' | 'left' | 'right'
  interactive?: boolean
  className?: string
  onShow?: () => void
  onHide?: () => void
}

type TriggerProps = {
  ref: (_node: any) => void
  className?: string
  onClick?: () => void
  onMouseEnter?: () => void
  onMouseLeave?: () => void
}

export default function Tooltip({
  children,
  content,
  disabled = false,
  trigger = 'hover',
  placement = 'top',
  interactive = false,
  className = '',
  onShow,
  onHide,
}: TooltipProps) {
  const [isOpen, setIsOpen] = useState(false)
  const [referenceElement, setReferenceElement] = useState<HTMLElement | null>(null)
  const [popperElement, setPopperElement] = useState<HTMLDivElement | null>(null)
  const [isMobile, setIsMobile] = useState(false)
  const updateTimeoutRef = useRef<number | null>(null)
  const [isBrowser, setIsBrowser] = useState(false)

  // Check if we're in browser environment
  useEffect(() => {
    setIsBrowser(true)
  }, [])

  // Check if we're on mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768) // md breakpoint in Tailwind
    }

    checkMobile()
    window.addEventListener('resize', checkMobile)
    return () => window.removeEventListener('resize', checkMobile)
  }, [])

  const { styles, attributes, update } = usePopper(referenceElement, popperElement, {
    placement,
    strategy: 'fixed', // Use fixed positioning to avoid scroll container issues
    modifiers: [
      { name: 'offset', options: { offset: [0, 8] } },
      {
        name: 'preventOverflow',
        options: {
          padding: 16,
          // Only set boundary to document.body in the browser
          ...(isBrowser ? { boundary: document.body } : {}),
          altAxis: true, // Prevent overflow on both axes
        },
      },
      {
        name: 'flip',
        options: {
          padding: 16,
          fallbackPlacements: ['top', 'bottom', 'left', 'right'],
          flipVariations: true,
        },
      },
      // This modifier ensures the tooltip stays within viewport bounds
      {
        name: 'hide',
        enabled: true,
      },
    ],
  })

  // Schedule periodic updates to handle UI changes
  const scheduleUpdate = useCallback(() => {
    if (updateTimeoutRef.current) {
      window.clearTimeout(updateTimeoutRef.current)
    }

    if (update && isOpen) {
      // Immediate update
      update()

      // Schedule additional updates to catch any delayed layout changes
      updateTimeoutRef.current = window.setTimeout(() => {
        update()
        updateTimeoutRef.current = window.setTimeout(() => {
          update()
        }, 300)
      }, 100)
    }
  }, [update, isOpen])

  // Update position when tooltip is opened
  useEffect(() => {
    if (isOpen && update) {
      // Run update in the next frame to ensure the tooltip is rendered
      const frameId = requestAnimationFrame(() => {
        update()
        onShow?.()
        scheduleUpdate()
      })
      return () => {
        cancelAnimationFrame(frameId)
        if (updateTimeoutRef.current) {
          window.clearTimeout(updateTimeoutRef.current)
        }
      }
    } else if (!isOpen) {
      onHide?.()
    }
  }, [isOpen, update, onShow, onHide, scheduleUpdate])

  // Listen for DOM mutations that might affect positioning
  useEffect(() => {
    if (!isOpen || !update || !isBrowser) return

    // Create a MutationObserver to detect DOM changes
    const observer = new MutationObserver((mutations) => {
      // Check if any mutation is outside of the popper element
      // to avoid infinite loops when popper updates its own styles
      const hasRelevantMutation = mutations.some((mutation) => {
        if (!popperElement) return true
        if (
          mutation.target === popperElement ||
          popperElement.contains(mutation.target)
        ) {
          return false
        }
        return true
      })

      if (hasRelevantMutation) {
        scheduleUpdate()
      }
    })

    // Start observing the document for any changes
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['style', 'class'],
    })

    return () => observer.disconnect()
  }, [isOpen, update, scheduleUpdate, isBrowser, popperElement])

  // Reposition when the popup's own size changes, e.g. when content loaded
  // after opening (the ayah tefsir) makes it taller.
  useEffect(() => {
    if (!isOpen || !popperElement || typeof ResizeObserver === 'undefined') return

    const observer = new ResizeObserver(() => scheduleUpdate())
    observer.observe(popperElement)
    return () => observer.disconnect()
  }, [isOpen, popperElement, scheduleUpdate])

  // Update position on scroll/resize
  useEffect(() => {
    if (!isOpen || !update || !isBrowser) return

    const handleUpdate = () => scheduleUpdate()
    window.addEventListener('scroll', handleUpdate, true)
    window.addEventListener('resize', handleUpdate)

    // Listen for clicks that might open dialogs or forms
    window.addEventListener('click', handleUpdate)

    return () => {
      window.removeEventListener('scroll', handleUpdate, true)
      window.removeEventListener('resize', handleUpdate)
      window.removeEventListener('click', handleUpdate)
      if (updateTimeoutRef.current) {
        window.clearTimeout(updateTimeoutRef.current)
      }
    }
  }, [isOpen, update, scheduleUpdate, isBrowser])

  // Handle click outside for click-triggered tooltips
  useEffect(() => {
    if (!isOpen || trigger !== 'click' || !isBrowser) return

    const handleClickOutside = (event: MouseEvent) => {
      if (
        popperElement &&
        !popperElement.contains(event.target as Node) &&
        referenceElement &&
        !referenceElement.contains(event.target as Node)
      ) {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [isOpen, trigger, popperElement, referenceElement, isBrowser])

  const handleTrigger = useCallback(() => {
    if (disabled) return
    setIsOpen(!isOpen)
  }, [disabled, isOpen])

  const handleMouseEnter = useCallback(() => {
    if (!disabled) setIsOpen(true)
  }, [disabled])

  const handleMouseLeave = useCallback(() => {
    if (!disabled && !interactive) setIsOpen(false)
  }, [disabled, interactive])

  // Set up trigger props based on trigger type
  const triggerProps: TriggerProps =
    trigger === 'click'
      ? {
          ref: setReferenceElement,
          onClick: handleTrigger,
          className: children.props.className,
        }
      : {
          ref: setReferenceElement,
          onMouseEnter: handleMouseEnter,
          onMouseLeave: handleMouseLeave,
          className: children.props.className,
        }

  return (
    <>
      {React.cloneElement(children, triggerProps)}
      {isOpen &&
        isBrowser &&
        createPortal(
          <div
            ref={setPopperElement}
            style={{
              ...styles.popper,
              zIndex: 1000,
              maxWidth: isMobile ? '90vw' : '70vw',
              maxHeight: '80vh',
              overflowY: 'auto',
              whiteSpace: 'normal',
              wordBreak: 'break-word',
              width: 'auto',
              boxSizing: 'border-box',
            }}
            {...attributes.popper}
            className={classNames(
              'rounded-xl bg-main border border-main p-3 text-sm text-main sacred-shadow',
              className,
            )}
          >
            <div className="w-full">{content}</div>
          </div>,
          document.body,
        )}
    </>
  )
}
